import { EventsController } from "Global/Dom/EventsController";
import { Event } from "../../../Global/Base/Events";
import { IDisposable } from "../../../Global/Base/IDisposable";
import { EnumUtils } from "../../../Global/Base/Utils/EnumUtils";
import { Localization } from "../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../UI/UI.ElementarControls/Utils/CustomElementUtils";
import { EventHelper } from "../../UI.BaseControls/Helpers/EventHelper";
import { ElementUtils } from "../../UI.ElementarControls/Helpers/ElementUtils";
import { BreadcrumbNavItemIconType } from "./IBreadcrumbNav";
import { IWindowable, WindowState } from "./UIManagerInterfaces";
import { IForm } from "./XGControls/IForm";

const ENUM_VALUE_LIST = EnumUtils.getEnumAsArray(BreadcrumbNavItemIconType);

export class BreadcrumbNavItem implements IDisposable {
    //#region Events
    public readonly onSelectedEvent: Event<BreadcrumbNavItem, BreadcrumbNavItem> = new Event<BreadcrumbNavItem, BreadcrumbNavItem>();
    public readonly onViewEvent: Event<BreadcrumbNavItem, BreadcrumbNavItem> = new Event<BreadcrumbNavItem, BreadcrumbNavItem>();
    public readonly onCloseEvent: Event<BreadcrumbNavItem, void> = new Event<BreadcrumbNavItem, void>();

    private _eventsController: EventsController = new EventsController();
    //#endregion Events

    //#region Listeners
    private _onClickListener: EventListener;
    private _onMouseOverListener: EventListener;
    private _onMouseOutListener: EventListener;
    private _iconElementClickListener: EventListener;
    private _keyDownListener: EventListener;
    private _keyUpListener: EventListener;
    private _onFocusListener: EventListener;
    private _onFocusOutListener: EventListener;
    private _onIconFocusListener: EventListener;
    //#endregion Listeners

    //#region Fields
    public element: HTMLElement;
    private _iconElement: HTMLElement;
    private _textElement: HTMLElement;
    private _canClose = true;
    private _isDisposed = false;

    private readonly _breadcrumbItemStyleClass = "breadcrumbnav__item";
    //#endregion Fields

    //#region Property
    public get isLast(): boolean {
        return this._iconElement.classList.contains(BreadcrumbNavItemIconType.close);
    }

    public get canClose(): boolean {
        return this._canClose;
    }

    public get isPreviewAllowed(): boolean {
        return this._iconElement.classList.contains(BreadcrumbNavItemIconType.view);
    }

    public get form(): IWindowable {
        return this._form;
    }

    public get isActive(): boolean {
        return this.element.classList.contains(this._breadcrumbItemStyleClass + "--active");
    }
    //#endregion Property

    //#region Constructors
    constructor(private _form: IWindowable, private _lockey: string, private _allowViewMode: boolean, private _formName?: string, public itemId?: number) {
        this._initialize();

        if (this.form instanceof IForm)
            this._eventsController.add(this.form.titleChanged, (_form: IForm, text: string) => ElementUtils.updateElementTextContent(this._textElement, text, _lockey));
    }
    //#endregion Constructors

    //#region Methods
    private _initialize(): void {
        this.element = createElement({ type: "span", classes: [this._breadcrumbItemStyleClass, this._breadcrumbItemStyleClass + "--active"] });
        this.element.tabIndex = 0;
        this._textElement = createElement({ type: "span", classes: [this._breadcrumbItemStyleClass + "__text"] });
        this._iconElement = createElement({ type: "span", classes: [this._breadcrumbItemStyleClass + "__icon", "icon", BreadcrumbNavItemIconType.close] });
        this._iconElement.tabIndex = 0;

        ElementUtils.updateElementTextContent(this._textElement, this.form instanceof IForm ? this.form.customTitle : undefined, this._lockey);

        if (this._formName) {
            this._textElement.setAttribute("data-formname", this._formName);
        }

        this.element.appendChild(this._textElement);
        this.element.appendChild(this._iconElement);

        if (this.form instanceof IForm && !this.form.canClose) {
            this._iconElement.classList.remove(BreadcrumbNavItemIconType.close);
            this._canClose = false;
        }

        this._initListeners();
    }

    private async _initListeners(): Promise<void> {
        if (this._iconElementClickListener) {
            this._iconElement.removeEventListener("click", this._iconElementClickListener);
            this._iconElementClickListener = null;
        }

        if (!this._onClickListener) {
            this.element.addEventListener("click", this._onClickListener = (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                this.setNavItemActive();
            });
        }
        // Pokud máme povolenou sys. operaci ADVBRDCRMB, a tato položka není aktivní ani poslední,
        // tak nastavíme listenery pro zobrazení ikony "oka" a pro "oko" samotné
        if (this._allowViewMode && !this.isActive && !this.isLast) {
            if (!this._onMouseOverListener) {
                this.element.addEventListener("mouseover", this._onMouseOverListener = (_e: MouseEvent) => {
                    this._addViewClickListener();
                });
            }
            if (!this._onMouseOutListener) {
                this.element.addEventListener("mouseout", this._onMouseOutListener = (_e: MouseEvent) => {
                    this.changeBreadcrumbNavItemIconType(BreadcrumbNavItemIconType.arrow, false);
                    this._iconElement.removeEventListener("click", this._iconElementClickListener);
                    this._iconElementClickListener = null;
                });
            }
            if (!this._onFocusListener) {
                this.element.addEventListener("focus", this._onFocusListener = (_e: FocusEvent) => {
                    this._addViewClickListener();
                });
            }
            if (!this._onFocusOutListener) {
                this.element.addEventListener("focusout", this._onFocusOutListener = (e: FocusEvent) => {
                    const leaving = !this.element.contains(e.relatedTarget as HTMLElement); // Když přechází focus na potomka (ikonu "oka"), tak nechci rušit listener
                    if (leaving) {
                        this.changeBreadcrumbNavItemIconType(BreadcrumbNavItemIconType.arrow, false);
                        this._iconElement.removeEventListener("click", this._iconElementClickListener);
                        this._iconElementClickListener = null;
                    }
                });
            }
            if (!this._onIconFocusListener) {
                this._iconElement.addEventListener("focus", this._onIconFocusListener = (_e: FocusEvent) => {
                    if (!this.isLast) {
                        this._addViewClickListener();
                    }
                });
            }
        } else {
            if (!this._iconElementClickListener) {
                this._iconElement.addEventListener("click", this._iconElementClickListener = (e: MouseEvent) => {
                    EventHelper.stopPropagation(e);
                    this._closeNavItem();
                });
            }
            // Odstranění listenerů pro zobrazení ikony "oka"
            if (this._onMouseOverListener) {
                this.element.removeEventListener("mouseover", this._onMouseOverListener);
                this._onMouseOverListener = null;
            }
            if (this._onMouseOutListener) {
                this.element.removeEventListener("mouseout", this._onMouseOutListener);
                this._onMouseOutListener = null;
            }
            if (this._onFocusListener) {
                this.element.removeEventListener("focus", this._onFocusListener);
                this._onFocusListener = null;
            }
            if (this._onFocusOutListener) {
                this.element.removeEventListener("focusout", this._onFocusOutListener);
                this._onFocusOutListener = null;
            }
        }
        this.element.addEventListener("keydown", this._keyDownListener = (e: KeyboardEvent) => {
            EventHelper.stopImmediatePropagation(e);
            if (e.key == "Enter") {
                if (e.target == this._iconElement) {
                    if (this.isLast) {
                        this._closeNavItem();
                    } else if (this.isPreviewAllowed) {
                        this._setNavItemToView();
                    }
                } else {
                    this.setNavItemActive();
                }
            }
            if (e.key == "Escape") {
                this._closeNavItem();
            }
        });
        this.element.addEventListener("keyup", this._keyUpListener = (e: KeyboardEvent) => {
            EventHelper.stopImmediatePropagation(e); // Některé další části poslouhají na keyup a pak to dělá neplechu
        });

        this._eventsController.add(this.form.stateChanged, (_, data) => {
            if (data.state == WindowState.closeDisabled) {
                this._iconElement.classList.toggle(BreadcrumbNavItemIconType.close, !data.value);
                this._canClose = !data.value;
            }
        });
    }

    private _closeNavItem(): void {
        this.onCloseEvent.trigger(this, null);
    }

    private _setNavItemToView(): void {
        this.changeBreadcrumbNavItemIconType(BreadcrumbNavItemIconType.arrow);
        this.onViewEvent.trigger(this, this);

        this.showCurrentForm();
    }

    public setNavItemActive(): void {
        this.onSelectedEvent.trigger(this, this);

        this.showCurrentForm();
    }

    public showCurrentForm(): void {
        this.setActiveState(true);
    }

    public changeBreadcrumbNavItemIconType(value: BreadcrumbNavItemIconType, initListeners: boolean = true): void {
        this._iconElement.classList.remove(...ENUM_VALUE_LIST);
        //Odstraneni icony pokud se jedna o posledni item a ma hideCloseButton
        if (value == null) {
            return;
        }

        if (value != BreadcrumbNavItemIconType.close || this._canClose)
            this._iconElement.classList.add(value); //Oteviram dalsi  formular a aktualni ma nastaveno hideCloseButton tak mu musim pridat iconu

        if (value === BreadcrumbNavItemIconType.close) {
            this._setIconTitle("GUI-508347"); // Zavřít
        } else if (value === BreadcrumbNavItemIconType.view) {
            this._setIconTitle("GUI-597156"); // Náhled
        } else {
            this._setIconTitle();
        }

        if (initListeners) {
            this._initListeners();
        }
    }

    private async _setIconTitle(titleLockey?: string): Promise<void> {
        if (!titleLockey) {
            this._iconElement.title = "";
            return;
        }
        const title = await Localization.getLocalizationItem(titleLockey);

        if (this.isDisposed()) { // Než doběhne await pro načtení lokalizace, může mezi tím proběhnout dispose, proto je třeba zkontrolovat.
            return;
        }

        this._iconElement.title = title.primaryText;
    }

    private _addViewClickListener(): void {
        if (this.isActive) {
            return;
        }
        this.changeBreadcrumbNavItemIconType(BreadcrumbNavItemIconType.view, false);
        if (!this._iconElementClickListener) {
            this._iconElement.addEventListener("click", this._iconElementClickListener = (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                this._setNavItemToView();
            });
        }
    }

    public setActiveState(isActive: boolean, changeIkon: boolean = false): void {
        if (isActive) {
            if (!this.isActive) {
                this.element.classList.add(this._breadcrumbItemStyleClass + "--active");
            }
            if (changeIkon) {
                this.changeBreadcrumbNavItemIconType(BreadcrumbNavItemIconType.close);
            }
        } else {
            if (this.isActive) {
                this.element.classList.remove(this._breadcrumbItemStyleClass + "--active");
                if (changeIkon) {
                    this.changeBreadcrumbNavItemIconType(BreadcrumbNavItemIconType.arrow);
                }
            }
        }
    }
    //#endregion Methods

    //#region Dispose

    /**
     * Vrací true pokud je instance již v disposed stavu.
     */
    public isDisposed(): boolean {
        return this._isDisposed;
    }

    public dispose(): void {
        this.onSelectedEvent.removeAll();
        this.onViewEvent.removeAll();
        this.onCloseEvent.removeAll();

        this._iconElement?.removeEventListener("focus", this._onIconFocusListener);
        this._iconElement?.removeEventListener("click", this._iconElementClickListener);
        this._iconElement?.remove();

        this.element?.removeEventListener("click", this._onClickListener);
        this.element?.removeEventListener("mouseover", this._onMouseOverListener);
        this.element?.removeEventListener("mouseout", this._onMouseOutListener);
        this.element?.removeEventListener("keydown", this._keyDownListener);
        this.element?.removeEventListener("keyup", this._keyUpListener);
        this.element?.removeEventListener("focus", this._onFocusListener);
        this.element?.removeEventListener("focusout", this._onFocusOutListener);
        this.element?.removeEventListener("focusout", this._onIconFocusListener);
        this.element?.remove();

        this._eventsController.dispose();
        this._isDisposed = true;
    }
    //#endregion Dispose
}
